import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StyledEngineProvider, ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import { useTheme } from '../../hooks/use-theme'
import { getMuiChartThemeColors } from './chartTheme'
import ErpLineChart, { type ErpLineChartProps } from './ErpLineChart'
import ErpBarChart, { type ErpBarChartProps } from './ErpBarChart'
import ErpPieChart, { type ErpPieChartProps } from './ErpPieChart'
import ErpChartCard from './ErpChartCard'

export type ErpChartVariant = 'line' | 'area' | 'bar' | 'barHorizontal' | 'pie' | 'donut'

/** @deprecated Use AnalyticsLineChart, AnalyticsBarChart, or AnalyticsDonutChart instead. */

type BaseProps = {
 variant: ErpChartVariant
 height?: number
 bare?: boolean
 title?: string
 className?: string
}

type LineProps = BaseProps & {
 variant: 'line' | 'area'
 categories: string[]
 series: ErpLineChartProps['series']
 showLegend?: boolean
 valueFormatter?: ErpLineChartProps['valueFormatter']
}

type BarProps = BaseProps & {
 variant: 'bar' | 'barHorizontal'
 categories: string[]
 series: ErpBarChartProps['series']
 showLegend?: boolean
 zeroBarMuted?: boolean
 valueFormatter?: ErpBarChartProps['valueFormatter']
}

type PieProps = BaseProps & {
 variant: 'pie' | 'donut'
 pieData: ErpPieChartProps['data']
 showLegend?: boolean
 valueFormatter?: ErpPieChartProps['valueFormatter']
}

export type ErpChartProps = LineProps | BarProps | PieProps

/** @deprecated Use AnalyticsLineChart, AnalyticsBarChart, or AnalyticsDonutChart instead. */
const ErpChart: React.FC<ErpChartProps> = (props) => {
 const { height = 300, bare = false, title, className } = props

 let chart: React.ReactNode = null

 if (props.variant === 'line' || props.variant === 'area') {
  chart = (
   <ErpLineChart
    categories={props.categories}
    series={props.series.map((s) => ({ ...s, area: props.variant === 'area' || s.area !== false }))}
    height={height}
    showLegend={props.showLegend}
    valueFormatter={props.valueFormatter}
    className={bare ? className : undefined}
   />
  )
 } else if (props.variant === 'bar' || props.variant === 'barHorizontal') {
  chart = (
   <ErpBarChart
    categories={props.categories}
    series={props.series}
    height={height}
    layout={props.variant === 'barHorizontal' ? 'horizontal' : 'vertical'}
    showLegend={props.showLegend}
    zeroBarMuted={props.zeroBarMuted}
    valueFormatter={props.valueFormatter}
    className={bare ? className : undefined}
   />
  )
 } else if (props.variant === 'pie' || props.variant === 'donut') {
  chart = (
   <ErpPieChart
    data={props.pieData}
    height={height}
    variant={props.variant === 'donut' ? 'donut' : 'pie'}
    showLegend={props.showLegend ?? true}
    valueFormatter={props.valueFormatter}
    className={bare ? className : undefined}
   />
  )
 }

 if (bare) return chart

 return (
  <ErpChartCard title={title} className={className}>
   {chart}
  </ErpChartCard>
 )
}

export default ErpChart

function ChartsProviderInner({ children }: { children: React.ReactNode }) {
 const { resolvedTheme } = useTheme()
 const { i18n } = useTranslation()

 const muiTheme = useMemo(() => {
  const direction = i18n.dir() === 'rtl' ? 'rtl' : 'ltr'
  const mode = resolvedTheme === 'dark' ? 'dark' : 'light'
  return createTheme({
   direction,
   palette: {
    mode,
    background: { default: 'transparent', paper: 'transparent' },
    ...getMuiChartThemeColors(mode),
   },
   typography: { fontFamily: 'inherit' },
  })
 }, [resolvedTheme, i18n])

 return (
  <StyledEngineProvider injectFirst>
   <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
  </StyledEngineProvider>
 )
}

export function ChartsProvider({ children }: { children: React.ReactNode }) {
 return <ChartsProviderInner>{children}</ChartsProviderInner>
}

export { ErpChart, ErpLineChart, ErpBarChart, ErpPieChart, ErpChartCard }
